import React, { useState, useCallback } from "react";
import {
  Grid,
  List,
  ListItem,
  ListItemText,
  Typography,
  Button,
  Modal,
  Box,
  CircularProgress,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DonutChart from "../doughnutChart";
import LegendText from "../legendText";
import { HeaderBox } from "./styles";
import { useDropzone } from "react-dropzone";
import { useUserData } from "components/customHooks/userDataContext";
import { processCareOpptFile } from "apiHandler/actions/clinical";

const Header = ({ visitData = [] }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [processingSummary, setProcessSummary] = useState(null);
  const userData = useUserData();
  const token = userData?.access_token;

  // Drag & Drop Handling
  const onDrop = useCallback((acceptedFiles) => {
    const selectedFile = acceptedFiles[0];

    if (selectedFile && selectedFile.type === "text/csv") {
      setFile(selectedFile);
    } else {
      alert("Please upload a valid CSV file.");
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ".csv",
    multiple: false,
  });

  const handleUpload = async () => {
    if (!file) {
      alert("Please select a file first.");
      return;
    }

    setUploading(true);

    const formData = new FormData();
    formData.append("file", file);
    formData.append(
      "request_json",
      JSON.stringify({
        type: "CareOpportunity",
        practice_id: userData?.selected_practice,
      })
    );

    try {
      const response = await processCareOpptFile(formData, token);

      // Retrieve the X-Processing-Summary header
      const xProcessingSummary = response.headers["x-processing-summary"];

      if (xProcessingSummary) {
        setProcessSummary(JSON.parse(xProcessingSummary));
      }
    } catch (error) {
      console.log("error: ", error);
    } finally {
      setUploading(false);
    }
  };

  const onClose = () => {
    if (uploading) {
      return null;
    } else {
      setOpen(false);
      setFile(null);
      setProcessSummary(null);
    }
  };

  return (
    <HeaderBox>
      <Grid container spacing={2}>
        {/* Left Side - Visit Types */}
        <Grid item xs={6}>
          <Typography fontSize={18} fontWeight={600} gutterBottom>
            Visits
          </Typography>
          <List>
            <ListItem>
              <ListItemText
                primary={
                  <Typography fontSize={14}>
                    <strong>Annual visits</strong> for all adult patients
                  </Typography>
                }
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary={
                  <Typography fontSize={14}>
                    <strong>Well child visits</strong> for patients ages 4 and
                    under
                  </Typography>
                }
              />
            </ListItem>
            {/* Upload Button */}
            <ListItem>
              <Button
                variant="contained"
                color="primary"
                startIcon={<CloudUploadIcon />}
                onClick={() => setOpen(true)}
              >
                Upload CSV
              </Button>
            </ListItem>
          </List>
        </Grid>

        {/* Right Side - Donut Chart */}
        <Grid borderLeft="1px solid #E5E4E2" item xs={3}>
          <DonutChart />
        </Grid>
        <Grid item xs={3}>
          <Typography
            borderBottom="1px solid #E5E4E2"
            fontSize={18}
            gutterBottom
          >
            Preventative Visit Progress
          </Typography>

          <Typography variant="body2" color="textSecondary">
            38 total visits
          </Typography>
          <List>
            {visitData.map((item, index) => (
              <ListItem key={index}>
                <LegendText text={item.text} color={item.color} />
              </ListItem>
            ))}
          </List>
        </Grid>
      </Grid>

      {/* Upload Modal */}
      <Modal open={open} onClose={onClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "white",
            p: 4,
            boxShadow: 24,
            borderRadius: 2,
            minWidth: 400,
            textAlign: "center",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Upload CSV File
          </Typography>

          <Box
            {...getRootProps()}
            sx={{
              border: "2px dashed rgb(169, 184, 199)",
              borderRadius: 2,
              p: 4,
              mb: 2,
              cursor: "pointer",
              backgroundColor: isDragActive ? "#e3f2fd" : "transparent",
            }}
          >
            <input {...getInputProps()} />
            <Box>
              <CloudUploadIcon color="secondary" />
              {file ? (
                <Typography variant="body2"> {file.name}</Typography>
              ) : (
                <Typography variant="body1">
                  {isDragActive
                    ? "Drop the file here..."
                    : "Drag & drop a CSV file here or click to select"}
                </Typography>
              )}
            </Box>
          </Box>
          {processingSummary && (
            <>
              <Typography variant="body2">
                Total Records: {processingSummary.total_records}
              </Typography>
              <Typography variant="body2">
                Successful Records: {processingSummary.successful_records}
              </Typography>
              <Typography variant="body2">
                Failed Records: {processingSummary.failed_records}
              </Typography>
            </>
          )}
          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disabled={uploading}
              fullWidth
            >
              {uploading ? <CircularProgress size={24} /> : "Upload"}
            </Button>
            {processingSummary && (
              <Button
                variant="contained"
                color="info"
                onClick={onClose}
                fullWidth
                sx={{ mt: 2 }}
              >
                Close
              </Button>
            )}
          </Box>
        </Box>
      </Modal>
    </HeaderBox>
  );
};

export default Header;
